/**
 * Real-Time Telemetry service.
 *
 * Owns all data access for the Real-Time Telemetry tab (Live and History
 * sub-views). All methods call real backend endpoints via realTimeDeviceService.
 */

import realTimeDeviceService from './realTimeDeviceService';

// ── Reference data ──────────────────────────────────────────────────────────

export const TELEMETRY_METRIC_OPTIONS = [
  { value: 'rsrp', label: 'RSRP' },
  { value: 'rsrq', label: 'RSRQ' },
  { value: 'sinr', label: 'SINR' },
  { value: 'throughput', label: 'Throughput' },
  { value: 'serving', label: 'Serving Cell' },
  { value: 'neighbor', label: 'Neighbor Cell' },
];

export const TELEMETRY_RAT_OPTIONS = [
  { value: 'all', label: 'All RATs' },
  { value: 'lte', label: 'LTE' },
  { value: '5g_nsa', label: '5G NSA' },
  { value: '5g_sa', label: '5G SA' },
];

export const TELEMETRY_TIME_RANGES = [
  { value: '5m',  label: '5 min' },
  { value: '15m', label: '15 min' },
  { value: '1h',  label: '1 hour' },
  { value: '6h',  label: '6 hours' },
];

// Converts time range label to milliseconds.
const RANGE_MS = {
  '5m':  5  * 60 * 1000,
  '15m': 15 * 60 * 1000,
  '1h':  60 * 60 * 1000,
  '6h':  6  * 60 * 60 * 1000,
};

// Maps backend actionKey to a short display label for the History table.
const ACTION_KEY_LABEL = {
  'signal-strength-rsrp':  'RSRP',
  'signal-strength-rsrq':  'RSRQ',
  'signal-strength-sinr':  'SINR',
  'serving-cell-metrics':  'Serving Cell',
  'neighbor-cell-metrics': 'Neighbor Cell',
  'throughput-performance':'Throughput',
  'get-throughput':        'Throughput',
};

// ── Service ─────────────────────────────────────────────────────────────────

class TelemetryService {
  /**
   * Return sessions available for live monitoring (active sessions).
   * Returns: Promise<Array<{ sessionId, systemId, status }>>
   */
  getMonitorableSessions() {
    return realTimeDeviceService.getActiveSessions().then((sessions) =>
      sessions.map((s) => ({
        sessionId:    s.sessionId,
        systemId:     s.systemId,
        status:       s.status,
        startTime:    s.startTime,
        durationMins: s.durationMins,
      }))
    );
  }

  /**
   * Return completed sessions available for history browsing.
   * Returns: Promise<Array<{ sessionId, systemId, status, startTime, endTime, durationMins }>>
   */
  getCompletedSessionsForHistory() {
    return realTimeDeviceService.getCompletedSessions().then((sessions) =>
      sessions.map((s) => ({
        sessionId:    s.sessionId,
        systemId:     s.systemId,
        status:       s.status,
        startTime:    s.startTime,
        endTime:      s.endTime,
        durationMins: s.durationMins,
      }))
    );
  }

  /**
   * Return live telemetry snapshot for a given session.
   * Calls GET /rtdm/sessions/{systemId}/{sessionId}/telemetry/latest
   * Returns the transformed shape, or null if no data is available.
   *
   * Returns: Promise<{ throughput, signal, servingCell, neighborCells, networkContext, lastUpdated } | null>
   */
  async getLiveTelemetry(sessionId, systemId) {
    const now = new Date().toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });

    if (!systemId || !sessionId) return null;

    try {
      const raw = await realTimeDeviceService.getLiveTelemetry(systemId, sessionId);
      if (raw && raw.data && Object.keys(raw.data).length > 0) {
        return _transformLiveTelemetry(raw.data, now);
      }
    } catch (_) {}

    return null;
  }

  /**
   * Return historical telemetry for the History view.
   * Calls GET /rtdm/sessions/{systemId}/{sessionId}/telemetry with time range filters.
   *
   * Params: { sessionId, systemId, timeRange, metrics: string[], rat }
   * Returns: Promise<{ signalSeries, throughputSeries, rows }>
   */
  async getHistory({ sessionId, systemId, timeRange = '15m', metrics = [], rat = 'all' } = {}) {
    const empty = { signalSeries: [], throughputSeries: [], rows: [] };
    if (!sessionId || !systemId) return empty;

    const rangeMs = RANGE_MS[timeRange] || RANGE_MS['15m'];
    const to   = new Date().toISOString();
    const from = new Date(Date.now() - rangeMs).toISOString();

    let allEntries = [];
    try {
      const res = await realTimeDeviceService._fetch(
        `/sessions/${encodeURIComponent(systemId)}/${encodeURIComponent(sessionId)}/telemetry?page=0&size=500`
      );
      if (res && Array.isArray(res.rows)) {
        allEntries = res.rows;
      }
    } catch (_) {
      return empty;
    }

    // ── Transform each entry into a flat history row ──
    const metricSet = new Set(
      metrics.length > 0 ? metrics : ['rsrp', 'rsrq', 'sinr', 'throughput', 'serving', 'neighbor']
    );

    const ACTION_KEY_TO_METRIC = {
      'signal-strength-rsrp':  'rsrp',
      'signal-strength-rsrq':  'rsrq',
      'signal-strength-sinr':  'sinr',
      'serving-cell-metrics':  'serving',
      'neighbor-cell-metrics': 'neighbor',
      'throughput-performance':'throughput',
      'get-throughput':        'throughput',
    };

    const rows = [];
    const signalByKey  = {};   // { rsrp: [{x, y}], rsrq: [...], sinr: [...] }
    const ulSeries     = [];
    const dlSeries     = [];

    for (const entry of allEntries) {
      const { actionKey, commonHeader = {}, payload = {} } = entry;
      const metricKey = ACTION_KEY_TO_METRIC[actionKey];

      // Skip metrics not in the selected set.
      if (metricKey && !metricSet.has(metricKey)) continue;

      // Skip by RAT filter.
      const entryRat = (commonHeader.wwanTech || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
      if (rat !== 'all' && entryRat && entryRat !== rat.toLowerCase()) continue;

      const ts = commonHeader.deviceTimestampEpochSec != null
        ? commonHeader.deviceTimestampEpochSec * 1000
        : commonHeader.receivedAt
        ? new Date(commonHeader.receivedAt).getTime()
        : Date.now();
      const mccMnc = (commonHeader.mcc != null && commonHeader.mnc != null)
        ? `${commonHeader.mcc}-${commonHeader.mnc}` : null;

      // ── Build flat row ──
      const row = {
        timestamp:  ts,
        action:     ACTION_KEY_LABEL[actionKey] || actionKey,
        rat:        _formatWwanTech(commonHeader.wwanTech),
        lteValue:   null,
        nr5gValue:  null,
        ul:         null,
        dl:         null,
        rtt:        null,
        pci:        commonHeader.servingPci ?? null,
        mccMnc,
        error:      payload.error || null,
      };

      if (actionKey.startsWith('signal-strength-')) {
        row.lteValue  = payload.lteValue  != null ? `${payload.lteValue} ${_signalUnit(actionKey)}`  : null;
        row.nr5gValue = payload.nr5gValue != null ? `${payload.nr5gValue} ${_signalUnit(actionKey)}` : null;
      } else if (actionKey === 'serving-cell-metrics') {
        row.lteValue  = payload.lteValue  != null ? `${payload.lteValue} dBm` : null;
        row.nr5gValue = payload.nr5gValue != null ? `${payload.nr5gValue} dBm` : null;

        // Accumulate chart series for signal metrics.
        const sk = ACTION_KEY_TO_METRIC[actionKey]; // rsrp | rsrq | sinr
        if (!signalByKey[sk]) signalByKey[sk] = [];
        const yVal = payload.lteValue ?? payload.nr5gValue;
        if (yVal != null) signalByKey[sk].push({ x: ts, y: yVal });

      } else if (actionKey === 'throughput-performance' || actionKey === 'get-throughput') {
        row.ul  = payload.ulPeakBps  != null ? +(payload.ulPeakBps  / 1_000_000).toFixed(1) : null;
        row.dl  = payload.dlPeakBps  != null ? +(payload.dlPeakBps  / 1_000_000).toFixed(1) : null;
        row.rtt = payload.rttMs ?? null;

        if (payload.ulPeakBps != null) ulSeries.push({ x: ts, y: +(payload.ulPeakBps / 1_000_000).toFixed(1) });
        if (payload.dlPeakBps != null) dlSeries.push({ x: ts, y: +(payload.dlPeakBps / 1_000_000).toFixed(1) });
      }

      rows.push(row);
    }

    // Sort rows ascending by timestamp for the table.
    rows.sort((a, b) => a.timestamp - b.timestamp);

    // ── Build chart series ──
    const SIGNAL_META = {
      rsrp: { name: 'RSRP (dBm)',  color: '#4e9af1' },
      rsrq: { name: 'RSRQ (dB)',   color: '#f1a84e' },
      sinr: { name: 'SINR (dB)',   color: '#4ef1a8' },
    };

    const signalSeries = Object.entries(signalByKey)
      .filter(([k]) => metricSet.has(k))
      .map(([k, pts]) => ({
        name:  SIGNAL_META[k]?.name  || k.toUpperCase(),
        color: SIGNAL_META[k]?.color || '#888',
        data:  pts.sort((a, b) => a.x - b.x),
      }));

    const throughputSeries = metricSet.has('throughput') ? [
      ...(ulSeries.length > 0 ? [{ name: 'UL (Mbps)', color: '#9b59b6', data: ulSeries.sort((a, b) => a.x - b.x) }] : []),
      ...(dlSeries.length > 0 ? [{ name: 'DL (Mbps)', color: '#2ecc71', data: dlSeries.sort((a, b) => a.x - b.x) }] : []),
    ] : [];

    return { signalSeries, throughputSeries, rows };
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function _signalUnit(actionKey) {
  if (actionKey === 'signal-strength-rsrp') return 'dBm';
  return 'dB';
}

const WWAN_TECH_LABELS = {
  CELLULAR_WWAN_TECH_TYPE_LTE:    'LTE',
  CELLULAR_WWAN_TECH_TYPE_5G_NSA: '5G NSA',
  CELLULAR_WWAN_TECH_TYPE_5G_SA:  '5G SA',
};

function _formatWwanTech(wwanTech) {
  if (!wwanTech) return null;
  return WWAN_TECH_LABELS[wwanTech] || wwanTech;
}

/**
 * Transform the raw /telemetry/latest response into the RTTLive shape.
 * Raw shape: { [actionKey]: { commonHeader: {...}, payload: {...} } }
 */
function _transformLiveTelemetry(data, lastUpdated) {
  const entry = (key) => data[key] || {};
  const ch = (key) => (entry(key).commonHeader || {});
  const pl = (key) => (entry(key).payload || {});

  const rsrpPl = pl('signal-strength-rsrp');
  const rsrqPl = pl('signal-strength-rsrq');
  const sinrPl  = pl('signal-strength-sinr');
  const signal = {
    rsrp: { lte: rsrpPl.lteValue ?? null, nr5g: rsrpPl.nr5gValue ?? null },
    rsrq: { lte: rsrqPl.lteValue ?? null, nr5g: rsrqPl.nr5gValue ?? null },
    sinr: { lte: sinrPl.lteValue  ?? null, nr5g: sinrPl.nr5gValue  ?? null },
    refreshLabel: 'Every 10s',
  };

  const scCh = ch('serving-cell-metrics');
  const mccMnc = (scCh.mcc != null && scCh.mnc != null)
    ? `${scCh.mcc}-${scCh.mnc}` : null;
  const cellIdHex = scCh.cellGlobalIdentity != null
    ? `0x${scCh.cellGlobalIdentity.toString(16).toUpperCase()}` : null;
  const servingCell = {
    rat: _formatWwanTech(scCh.wwanTech),
    cellId: cellIdHex,
    earfcn: scCh.earfcn ?? null,
    arfcn: scCh.arfcn ?? null,
    pci: scCh.servingPci ?? null,
    bandwidth: null,
    band: null,
    tac: null,
    mccMnc,
    rsrp: rsrpPl.lteValue ?? rsrpPl.nr5gValue ?? null,
    rsrq: rsrqPl.lteValue ?? rsrqPl.nr5gValue ?? null,
    sinr: sinrPl.lteValue  ?? sinrPl.nr5gValue  ?? null,
    txPower: null,
    refreshLabel: 'Every 10s',
  };

  let neighborCells = { lte: [], nr5g: [], refreshLabel: 'Every 30s' };
  const neighborJsonStr = pl('neighbor-cell-metrics').neighborCellsJson;
  if (neighborJsonStr) {
    try {
      const parsed = JSON.parse(neighborJsonStr);
      if (Array.isArray(parsed)) {
        neighborCells.lte = parsed
          .filter((c) => (c.rat || '').toUpperCase() === 'LTE' || c.earfcn != null)
          .map((c, i) => ({ num: i + 1, arfcn: c.earfcn ?? c.arfcn, pci: c.pci, cgi: c.cgi || null, plmn: c.plmn || null, rsrp: c.rsrp, rsrq: c.rsrq }));
        neighborCells.nr5g = parsed
          .filter((c) => (c.rat || '').toUpperCase().includes('NR') || (c.arfcn != null && c.earfcn == null))
          .map((c, i) => ({ num: i + 1, arfcn: c.arfcn ?? c.earfcn, pci: c.pci, rsrp: c.rsrp, rsrq: c.rsrq, plmn: c.plmn || null, cgi: c.cgi || null }));
      } else if (parsed.lte || parsed.nr5g) {
        neighborCells.lte  = (parsed.lte  || []).map((c, i) => ({ num: i + 1, ...c }));
        neighborCells.nr5g = (parsed.nr5g || []).map((c, i) => ({ num: i + 1, ...c }));
      }
    } catch (_) {}
  }

  const tpPl = pl('throughput-performance');
  const throughput = {
    ulMbps: tpPl.ulPeakBps != null ? +(tpPl.ulPeakBps / 1_000_000).toFixed(1) : null,
    dlMbps: tpPl.dlPeakBps != null ? +(tpPl.dlPeakBps / 1_000_000).toFixed(1) : null,
    rttMs: tpPl.rttMs ?? null,
    jitterMs: tpPl.jitterMs ?? null,
    packetLossPct: tpPl.packetLossPct ?? null,
    testType: 'UL + DL',
    refreshLabel: 'Every 5 min',
    ulSeries: [],
    dlSeries: [],
  };

  const anyHeader = ch('signal-strength-rsrp') || ch('serving-cell-metrics') || {};
  const networkContext = {
    rat: _formatWwanTech(anyHeader.wwanTech),
    mcc: anyHeader.mcc != null ? String(anyHeader.mcc) : null,
    mnc: anyHeader.mnc != null ? String(anyHeader.mnc) : null,
    cgi: anyHeader.cellGlobalIdentity != null
      ? `0x${anyHeader.cellGlobalIdentity.toString(16).toUpperCase()}` : null,
    earfcn: anyHeader.earfcn ?? null,
    arfcn: anyHeader.arfcn ?? null,
    servingPci: anyHeader.servingPci ?? null,
    latitude: anyHeader.latitude != null ? `${anyHeader.latitude}°` : null,
    longitude: anyHeader.longitude != null ? `${anyHeader.longitude}°` : null,
    timezone: anyHeader.timezoneAlpha || null,
  };

  return { throughput, signal, servingCell, neighborCells, networkContext, lastUpdated };
}

export default new TelemetryService();
